import {Grid} from '@material-ui/core';
import React, {useEffect} from 'react';
import {useSelector} from 'react-redux';
import {ReducerState} from 'rootReducer';
import {FormRowGrid, GridRowProps} from '../../../../shared/GridRow';
import {QuoteFormElement} from '../../../../shared/QuoteFormElement';
import {FormWrapper} from '../../../../shared/QuoteFormWrapper';
import {ConditionalSection, QuoteFormConditionalSection} from 'Quoting/components/shared/QuoteFormConditionalSection';
import {
  GetQuoteFieldValueFn,
  UpdateQuoteFieldValuesFn,
  useQuoteField,
} from 'Quoting/components/hooks/useHandleQuoteFieldChange';
import {getBenefitScheduleBenefitStructurePropertyPaths, getParentSchedulePath} from '../Common/PropertyPaths';
import {getFieldError, getValidationRuleset} from '../rules/rulesValidator';
import {getByKeyPath, isDefined} from 'Quoting/utils/utilities';
import {useQuoteFormValidation} from 'Quoting/components/hooks/useQuoteFormValidation';
import {Map as IMap, List as IList} from 'immutable';
import {getSelfServeSupportedMaxNumberOfLives} from 'Quoting/utils/validationHelpers';

const benefitStructureIndices = [0, 1, 2];

const getBenefitAmountOptions = (numOfPlans: number, numOfLives: number, plans?: IList<IMap<string, any>>) => {
  const options = [
    {displayValue: '$10,000', code: '10000', orderNo: 0},
    {displayValue: '$15,000', code: '15000', orderNo: 1, disabled: numOfPlans === 2 && numOfLives < 250},
    {displayValue: '$20,000', code: '20000', orderNo: 2},
  ];
  if (numOfLives >= 250 && numOfLives <= getSelfServeSupportedMaxNumberOfLives()) {
    options.push({displayValue: '$30,000', code: '30000', orderNo: 3});
  }

  // Apply cross-plan restrictions only when there are 2 or more plans
  if (numOfPlans >= 2 && numOfLives >= 250) {
    const planAmounts = plans.slice(0, numOfPlans)
      .map(plan => getByKeyPath(plan, benefitAmountPath) as number).toArray();

    const has15k = planAmounts.some(amount => amount === Number(options[1].code));
    const has10kOr20k = planAmounts.some(amount => amount === Number(options[0].code) || amount === Number(options[2].code));

    if (has15k) { // If either plan is 15k, disable 10k and 20k options
      options.forEach(option => {
        if (option.code === '10000' || option.code === '20000') {
          option.disabled = true;
        }
      });
    }
    if (has10kOr20k) { // If either plan is 10k or 20k, disable 15k option
      const option15k = options.find(option => option.code === '15000');
      if (option15k) {
        option15k.disabled = true;
      }
    }
  }

  return options;
};

const benefitAmountPath = 'coverageDefs[0].benefitStructures[0].benefitAmount.amount';
const getPathToBenefitAmount = (planIndex: number) => `benefitPackagingDetail.plans[${planIndex}].${benefitAmountPath}`;

const handleBenefitAmountRestriction = (
  benefitAmountOptions: ReturnType<typeof getBenefitAmountOptions>,
  getQuoteFieldValue: GetQuoteFieldValueFn,
  updateQuoteFieldValues: UpdateQuoteFieldValuesFn,
  numberOfLives: number
) => {
  const plans = getQuoteFieldValue<IList<IMap<string ,any>>>('benefitPackagingDetail.plans');
  if (!IList.isList(plans)) return;

  const planAmounts = [0, 1].map(idx => {
    const plan = plans.get(idx);
    if (!plan) return undefined;
    return getByKeyPath(plan, benefitAmountPath) as number;
  });

  if (numberOfLives >= 250 && numberOfLives <= getSelfServeSupportedMaxNumberOfLives()) {
    if (planAmounts[0] === Number(benefitAmountOptions[0].code) 
        && planAmounts[1] === Number(benefitAmountOptions[0].code)) {
      updateQuoteFieldValues([
        {path: getPathToBenefitAmount(0), newValue: Number(benefitAmountOptions[0].code)},
        {path: getPathToBenefitAmount(1), newValue: Number(benefitAmountOptions[2].code)},
      ]);
      return;
    } else if (planAmounts[0] === Number(benefitAmountOptions[1].code) 
        && planAmounts[1] === Number(benefitAmountOptions[1].code)) {
      // If both plans have 15k amount, set one to 15k and other to 30k respectively
      updateQuoteFieldValues([
        {path: getPathToBenefitAmount(0), newValue: Number(benefitAmountOptions[1].code)},
        {path: getPathToBenefitAmount(1), newValue: Number(benefitAmountOptions[3].code)},
      ]);
      return;
    } else if (planAmounts[0] === Number(benefitAmountOptions[2].code) 
        && planAmounts[1] === Number(benefitAmountOptions[2].code)) {
      updateQuoteFieldValues([
        {path: getPathToBenefitAmount(0), newValue: Number(benefitAmountOptions[2].code)},
        {path: getPathToBenefitAmount(1), newValue: Number(benefitAmountOptions[0].code)},
      ]);
      return;
    }

    // Otherwise, run the original logic for each plan with the 15k amount
    const planIndicesWith15kOption = [0, 1].filter(idx => planAmounts[idx] === Number(benefitAmountOptions[1].code));
    planIndicesWith15kOption.forEach(planIndex => {
      if (planIndex === 0) {
        updateQuoteFieldValues([
          {
            path: getPathToBenefitAmount(1),
            newValue: Number(benefitAmountOptions[3].code),
          }
        ]);
      } else if (planIndex === 1) {
        updateQuoteFieldValues([
          {
            path: getPathToBenefitAmount(0),
            newValue: Number(benefitAmountOptions[3].code),
          }
        ]);
      }
    });
    return;
  }

  // If both plans have 15k amount, set both to the first amount
  if (planAmounts[0] === Number(benefitAmountOptions[1].code) && planAmounts[1] === Number(benefitAmountOptions[1].code)) {
    updateQuoteFieldValues([
      {path: getPathToBenefitAmount(0), newValue: Number(benefitAmountOptions[0].code)},
      {path: getPathToBenefitAmount(1), newValue: Number(benefitAmountOptions[2].code)},
    ]);
    return;
  }

  // Otherwise, run the original logic for each plan with the 15K amount
  const planIndicesWithIncorrectOption = [0, 1].filter(
    idx => planAmounts[idx] === Number(benefitAmountOptions[1].code)
  );

  planIndicesWithIncorrectOption.forEach(planIndexWithIncorrectOption => {
    const selectedPlanAmount = getQuoteFieldValue<number>(getPathToBenefitAmount(planIndexWithIncorrectOption));

    if (selectedPlanAmount === Number(benefitAmountOptions[0].code)) {
      updateQuoteFieldValues([
        {
          path: getPathToBenefitAmount(planIndexWithIncorrectOption),
          newValue: Number(benefitAmountOptions[2].code),
        },
      ]);
    } else if (selectedPlanAmount === Number(benefitAmountOptions[2].code)) {
      updateQuoteFieldValues([
        {
          path: getPathToBenefitAmount(planIndexWithIncorrectOption),
          newValue: Number(benefitAmountOptions[0].code),
        },
      ]);
    } else if(selectedPlanAmount === Number(benefitAmountOptions[1].code)) {
       updateQuoteFieldValues([
        {
          path: getPathToBenefitAmount(planIndexWithIncorrectOption),
          newValue: Number(benefitAmountOptions[0].code),
        },
      ]);
    }
  });
};

export const CIBenefitSchedule = () => {
  const {getQuoteFieldValue, updateQuoteFieldValue, updateQuoteFieldValues} = useQuoteField();
  const handleValidationOnBlur = useQuoteFormValidation();

  const {selectedProduct, selectedPlan, quoteFormErrors} = useSelector((state: ReducerState) => state.quoteForm);
  const {BR_QuotingTool_EIS_Validation} = useSelector((state: ReducerState) => state.featureFlag);
  const numberOfLives = getQuoteFieldValue<number>('participationDetails.numberOfEligibleLives') ?? 0;
  const productCd = selectedProduct?.code ?? '';

  const IndividualBenefitAmountErrorKey = `${productCd}BenefitStructureIndividual.benefitAmount`;
  const situsStateCd = getQuoteFieldValue('riskStateCd') ?? '';
  const planCd = getQuoteFieldValue<IMap<string, any>>(
    `benefitPackagingDetail.plans[${selectedPlan?.planIndex ?? 0}]`
  )?.get('planCd');
  const isGVCI4andNH = planCd === 'GVCI4' && situsStateCd === 'NH';

  const benefitScheduleBenefitStructurePropertyPaths = getBenefitScheduleBenefitStructurePropertyPaths(
    benefitStructureIndices,
    selectedPlan?.planIndex,
    0
  );

  const IndividualBenefitAmountError = getFieldError(
    IndividualBenefitAmountErrorKey,
    getQuoteFieldValue<number>(benefitScheduleBenefitStructurePropertyPaths[0].benefitAmount),
    getValidationRuleset(selectedProduct?.code)
  );
  const gvci4NHBenefitAmount = 15000;

  const plans = getQuoteFieldValue<IList<IMap<string, any>>>('benefitPackagingDetail.plans');
  const numOfPlans = plans?.size || 0;
  const benefitAmountOptions = getBenefitAmountOptions(numOfPlans, numberOfLives, plans);

  useEffect(() => {
    if (isGVCI4andNH) {
      updateQuoteFieldValue(benefitScheduleBenefitStructurePropertyPaths[0].benefitAmount, gvci4NHBenefitAmount);
    }
  }, []);

  useEffect(() => {
    if (!isGVCI4andNH && numOfPlans === 2) {
      handleBenefitAmountRestriction(benefitAmountOptions, getQuoteFieldValue, updateQuoteFieldValues, numberOfLives);
    }
  }, [numOfPlans, isGVCI4andNH, numberOfLives]);

  const rows: GridRowProps[] = [
    {
      rowProps: {key: 'benefit-schedule-row-1'},
      title: 'PARTICIPANT',
      items: [
        {
          key: 'benefitStructures[0].benefitTypeCd',
          element: (
            <QuoteFormElement
              elementType="SelectV2"
              dropdownVariant="AttributeValue"
              searchKey={`${productCd}BenefitStructureIndividual.benefitTypeCd`}
              label="Benefit Type"
              value={getQuoteFieldValue<string>(benefitScheduleBenefitStructurePropertyPaths[0].benefitTypeCd)}
              id={benefitScheduleBenefitStructurePropertyPaths[0].benefitTypeCd}
              onChange={e =>
                updateQuoteFieldValue(benefitScheduleBenefitStructurePropertyPaths[0].benefitTypeCd, e.target.value)
              }
              disabled
              required
            />
          ),
          props: {xs: 6, sm: 6, md: 6},
        },
        {
          key: benefitScheduleBenefitStructurePropertyPaths[0].benefitAmount,
          element: (
            <QuoteFormElement
              elementType="Select"
              label="Benefit Amount"
              options={benefitAmountOptions}
              value={
                isGVCI4andNH
                  ? gvci4NHBenefitAmount
                  : getQuoteFieldValue<number>(benefitScheduleBenefitStructurePropertyPaths[0].benefitAmount)
              }
              id={benefitScheduleBenefitStructurePropertyPaths[0].benefitAmount}
              onChange={e => {
                updateQuoteFieldValue(
                  benefitScheduleBenefitStructurePropertyPaths[0].benefitAmount,
                  Number(e.target.value)
                );
              }}
              adornment="money"
              error={
                BR_QuotingTool_EIS_Validation
                  ? Boolean(quoteFormErrors?.[IndividualBenefitAmountErrorKey])
                  : isDefined(IndividualBenefitAmountError)
              }
              helperText={
                BR_QuotingTool_EIS_Validation && quoteFormErrors
                  ? quoteFormErrors[IndividualBenefitAmountErrorKey]
                  : IndividualBenefitAmountError
              }
              onBlur={() => {
                handleValidationOnBlur();
              }}
              required
              disabled={isGVCI4andNH}
            />
          ),
          props: {xs: 6, sm: 6, md: 6},
        },
      ],
    },
    {
      rowProps: {key: 'benefit-schedule-row-2'},
      title: 'SPOUSE',
      items: [
        {
          key: 'benefitStructures[1].benefitTypeCd',
          element: (
            <QuoteFormElement
              elementType="SelectV2"
              dropdownVariant="AttributeValue"
              searchKey={`${productCd}BenefitStructureSpouse.benefitTypeCd`}
              label="Benefit Type"
              value={getQuoteFieldValue<number>(benefitScheduleBenefitStructurePropertyPaths[1].benefitTypeCd)}
              id={benefitScheduleBenefitStructurePropertyPaths[1].benefitTypeCd}
              onChange={e =>
                updateQuoteFieldValue(benefitScheduleBenefitStructurePropertyPaths[1].benefitTypeCd, e.target.value)
              }
              disabled
              required
            />
          ),
          props: {xs: 6, sm: 6, md: 6},
        },
        {
          key: 'benefitStructures[1].individualAmtPct',
          element: (
            <QuoteFormElement
              elementType="SelectV2"
              dropdownVariant="AttributeValue"
              searchKey={`${productCd}BenefitStructureSpouse.individualAmtPct`}
              label="Percentage of Participant Amount"
              value={getQuoteFieldValue<number>(benefitScheduleBenefitStructurePropertyPaths[1].individualAmtPct)}
              id={benefitScheduleBenefitStructurePropertyPaths[1].individualAmtPct}
              onChange={e =>
                updateQuoteFieldValue(benefitScheduleBenefitStructurePropertyPaths[1].individualAmtPct, e.target.value)
              }
              disabled
              required
            />
          ),
          props: {xs: 6, sm: 6, md: 6},
        },
      ],
    },
    {
      rowProps: {key: 'benefit-schedule-row-3'},
      title: 'CHILDREN',
      items: [
        {
          key: 'benefitStructures[2].benefitTypeCd',
          element: (
            <QuoteFormElement
              elementType="SelectV2"
              dropdownVariant="AttributeValue"
              searchKey={`${productCd}BenefitStructureChild.benefitTypeCd`}
              label="Benefit Type"
              value={getQuoteFieldValue<number>(benefitScheduleBenefitStructurePropertyPaths[2].benefitTypeCd)}
              id={benefitScheduleBenefitStructurePropertyPaths[2].benefitTypeCd}
              onChange={e =>
                updateQuoteFieldValue(benefitScheduleBenefitStructurePropertyPaths[2].benefitTypeCd, e.target.value)
              }
              disabled
              required
            />
          ),
          props: {xs: 6, sm: 6, md: 6},
        },
        {
          key: 'benefitStructures[2].individualAmtPct',
          element: (
            <QuoteFormElement
              elementType="SelectV2"
              dropdownVariant="AttributeValue"
              searchKey={`${productCd}BenefitStructureChild.individualAmtPct`}
              label="Percentage of Participant Amount"
              value={getQuoteFieldValue<number>(benefitScheduleBenefitStructurePropertyPaths[2].individualAmtPct)}
              id={benefitScheduleBenefitStructurePropertyPaths[2].individualAmtPct}
              onChange={e =>
                updateQuoteFieldValue(benefitScheduleBenefitStructurePropertyPaths[2].individualAmtPct, e.target.value)
              }
              disabled
              required
            />
          ),
          props: {xs: 6, sm: 6, md: 6},
        },
      ],
    },
  ];

  return <FormRowGrid rows={rows} />;
};

export const LifeTimeMaximumBenefit = () => {
  const {getQuoteFieldValue, updateQuoteFieldValue} = useQuoteField();
  const {selectedPlan} = useSelector((state: ReducerState) => state.quoteForm);

  const benefitSchedulePropertyPath = `${getParentSchedulePath(
    selectedPlan?.planIndex
  )}.lifetimeMaximumDetails.maxBenefitAmountPct`;

  const rows: GridRowProps[] = [
    {
      rowProps: {key: 'benefit-schedule-row-4'},
      items: [
        {
          key: 'benefitSchedule.lifetimeMaximumDetails.maxBenefitAmountPct',
          element: (
            <QuoteFormElement
              elementType="SelectV2"
              dropdownVariant="AttributeValue"
              searchKey={'CILifetimeMaximumBenefit.maxBenefitAmountPct'}
              label="Maximum Benefit Amount (Per Insured)"
              value={getQuoteFieldValue<string>(benefitSchedulePropertyPath)}
              id={benefitSchedulePropertyPath}
              onChange={e => updateQuoteFieldValue(benefitSchedulePropertyPath, e.target.value)}
              disabled
              required
            />
          ),
          props: {xs: 6, sm: 6, md: 6},
        },
      ],
    },
  ];

  return <FormRowGrid rows={rows} />;
};

export const BenefitScheduleBlock = () => {
  const lifeTimeMaximumBenefit: ConditionalSection[] = [
    {
      Component: LifeTimeMaximumBenefit,
      key: 'lifeTimeMaxBenefit',
      label: 'LIFETIME MAXIMUM BENEFIT',
      isApplied: true,
      disabled: true,
    },
  ];

  return (
    <FormWrapper title="Benefit Schedule" collapsible withSeparator expanded>
      <Grid container direction="column">
        <CIBenefitSchedule />
        <QuoteFormConditionalSection sections={lifeTimeMaximumBenefit} />
      </Grid>
    </FormWrapper>
  );
};
